import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { IFlow, INode, INodeOutputContext, NodeType } from '../definitions/Nodes'
import { ExecutionEnvironmentMode } from '../definitions/NodesExecution'
import ValidationManager from '../managers/ValidationManager'

/**
 * timeline base
 *  timeline false
 *  timeline true
 */

class TimelineEngineClass {
    build = (flow: IFlow, mode?: ExecutionEnvironmentMode): ITimeline => {
        affirm(flow, 'Not flow was provided.')
        affirm(flow.nodes, 'The provided flow has null nodes')
        affirm(flow.nodes.every(algo.hasVal), 'Invalid nodes found inside flow')
        if (flow.type === 'sub-flow') {
            affirm(flow.nodes.filter(x => x.type === 'subFlow_start').length === 1, `Found more than one node of type 'subFlow_start': only one start node is allowed in each flow.`)
            affirm(flow.nodes.filter(x => x.type === 'subFlow_end').length === 1, `Found more than one node of type 'subFlow_end': only one start node is allowed in each flow.`)
        } else {
            affirm(flow.nodes.filter(x => x.type === 'start').length === 1, `Found more than one node of type 'start': only one start node is allowed in each flow.`)
        }

        ValidationManager.validate(flow, mode)

        const startingNodes = flow.nodes.filter(x => x.inputs.length === 0)
        const map: ITimelineMap = {
            paths: startingNodes.map(x => ({ uuid: x.uuid, foundAtDepths: [0] }))
        }

        for (let i = 0; i < startingNodes.length; i++) {
            const node = startingNodes[i]
            this._exploreNode(node, 1, [], flow, map)
        }

        // FOR DEBUG
        // if (Helper.isDev()) {
        //     map.paths.forEach(path => {
        //         const matchingNode = flow.nodes.find(node => node.uuid === path.uuid)
        //         if (matchingNode) console.log(`Node Name: ${matchingNode.name}, Depths: ${path.foundAtDepths.join(', ')}`)
        //     })
        // }

        const timeline = this._mapToTimeline(map, flow)
        return timeline
    }

    private _exploreNode = (node: INode, depth: number, explorePath: string[], flow: IFlow, map: ITimelineMap) => {
        affirm(node, 'Invalid node')
        affirm(depth, 'Invalid depth')
        affirm(explorePath, 'Invalid explore path')
        affirm(flow, 'Invalid flow')
        affirm(map, 'Invalid map')

        const newExplorePath = [...explorePath, node.uuid]
        affirm(newExplorePath.filter(x => x === node.uuid).length === 1, `Node "${node.name}" forms a circular dependency: "${newExplorePath.join(' -> ')}"`)

        const children = this._getChildren(flow.nodes, node)
        for (let i = 0; i < children.length; i++) {
            const child = children[i]
            let path = map.paths.find(x => x.uuid === child.uuid)
            if (!path) {
                path = { uuid: child.uuid, foundAtDepths: [] }
                map.paths.push(path)
            }
            path.foundAtDepths.push(depth)

            this._exploreNode(child, depth + 1, newExplorePath, flow, map)
        }
    }

    private _mapToTimeline = (map: ITimelineMap, flow: IFlow): ITimeline => {
        affirm(map, 'Invalid map')
        affirm(flow, 'Invalid flow')

        const timeline: ITimeline = { steps: [] }
        for (let i = 0; i < map.paths.length; i++) {
            const path = map.paths[i]
            const maxDepth = Math.max(...path.foundAtDepths)

            let step = timeline.steps.find(x => x.depth === maxDepth)
            if (!step) {
                step = { depth: maxDepth, nodes: [] }
                timeline.steps.push(step)
            }

            if (!step.nodes.some(x => x.uuid === path.uuid)) {
                const node = flow.nodes.find(x => x.uuid === path.uuid)
                step.nodes.push(node)
            }
        }

        timeline.steps.sort((a, b) => a.depth - b.depth)

        return timeline
    }

    private _getChildren = (nodes: INode[], node: INode): INode<NodeType>[] => {
        const children = nodes.filter(c => c.inputs.some(i => i.uuid === node.uuid))
        return children
    }

    handleForkNodes = (stepResults: INodeOutputContext<NodeType>[], timeline: ITimeline, stepIndex: number) => {
        const forkNodes = stepResults.filter(s => s.node.type === 'fork')
        forkNodes.forEach(forkNode => {
            const key = Object.keys(forkNode.output).find(k => forkNode.output[k] !== null)
            timeline.steps[stepIndex + 1].nodes = timeline.steps[stepIndex + 1].nodes.filter(n => !n.inputs.some(x => x.uuid === forkNode.node.uuid && x.outputKey !== key))

            for (let j = stepIndex + 2; j < timeline.steps.length; j++) {
                timeline.steps[j].nodes = timeline.steps[j].nodes.filter(n => n.inputs.some(i => timeline.steps[j - 1].nodes.some(m => m.uuid === i.uuid)))
            }
        })
    }

    print = (timeline: ITimeline) => {
        for (let i = 0; i < timeline.steps.length; i++) {
            const step = timeline.steps[i]
            console.log(`Timeline step: ${step.depth}`)
            console.log(`Nodes in step: ${step.nodes.map(x => x.name).join(', ')}\n`)
        }
    }
}

const TimelineEngine = new TimelineEngineClass()
export default TimelineEngine

export interface ITimelineStep {
    nodes: INode[]
    depth: number
}

export interface ITimeline {
    steps: ITimelineStep[]
}

export interface ITimelineMap {
    paths: {
        uuid: string
        foundAtDepths: number[]
    }[]
}
